import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';

const CustomizationModal = ({ isOpen, onClose, product }) => {
  const [formData, setFormData] = useState({
    text: '',
    color: '#000000',
    size: 'medium',
    material: 'standard',
    notes: '',
    uploadedImage: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);

  // Reset form when modal opens with new product
  useEffect(() => {
    if (isOpen && product) {
      setFormData({
        text: '',
        color: '#000000',
        size: 'medium',
        material: 'standard',
        notes: '',
        uploadedImage: null
      });
      setImagePreview(null);
    }
  }, [isOpen, product]);

  // Handle escape key and focus management
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Focus the modal when it opens
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setFormData(prev => ({
          ...prev,
          uploadedImage: file
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the customization data to your backend
    console.log('Customization data:', { product, formData });

    // Show success message (you could add a toast notification here)
    alert('Customization saved successfully!');
    onClose();
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      uploadedImage: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen || !product) return null;

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full max-w-2xl bg-gradient-to-br from-orange-50 to-orange-200 bg-opacity-95 shadow-2xl z-50 transform transition-all duration-300 ease-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          borderRadius: '10px 0 0 10px'
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        {/* Close Button - Positioned on left side, half outside */}
        <button
            onClick={onClose}
            className="fixed left-4 top-4 w-8 h-8 bg-black text-white rounded-full hover:bg-gray-800 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white z-50 flex items-center justify-center shadow-lg"
            aria-label="Close customization modal"
            >
            <X className="w-6 h-6" />
        </button>


        {/* Modal Header */}
        <div className="sticky top-0 bg-orange-50  p-8 z-10 border-b border-orange-200">
          <div className="text-center">
            <h2 id="modal-title" className="text-3xl font-bold text-teal-800 mb-2">
              Customize Product
            </h2>
            <div className="w-16 h-1 bg-orange-300 rounded-full mx-auto"></div>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-8 border-b border-orange-200">
          <div className="flex gap-4 p-4 bg-white bg-opacity-60 rounded-xl shadow-sm border border-orange-100">
            <div className="relative">
              <img
                src={product.image}
                alt={product.name}
                className="w-20 h-20 object-cover rounded-lg"
              />
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-800 text-lg mb-1">{product.name}</h3>
              <p className="text-sm text-gray-600 mb-2">{product.category}</p>
              <p className="text-lg font-bold text-teal-700">{product.price}</p>
            </div>
          </div>
        </div>

        {/* Customization Form */}
        <form
          onSubmit={handleSubmit}
          className="p-8 space-y-8 min-h-[700px] rounded-[25px] m-4 bg-gray-400 bg-opacity-20"
        >
          {/* Custom Text */}
          <div className="space-y-3">
            <label htmlFor="custom-text" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Custom Text
            </label>
            <input
              id="custom-text"
              type="text"
              name="text"
              value={formData.text}
              onChange={handleInputChange}
              placeholder="Enter your custom text..."
              className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500"
              maxLength={50}
            />
            <p className="text-xs text-gray-600 flex justify-between items-center">
              <span>Make it personal and unique</span>
              <span className="font-medium">{formData.text.length}/50</span>
            </p>
          </div>

          {/* Color Selection */}
          <div className="space-y-3">
            <label htmlFor="color-picker" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Text Color
            </label>
            <div className="flex items-center gap-4 p-4 bg-white bg-opacity-80 border border-orange-200 rounded-xl">
              <input
                id="color-picker"
                type="color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="w-16 h-16 border border-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
              <div>
                <span className="text-lg font-bold text-gray-800">{formData.color}</span>
                <p className="text-xs text-gray-600">Click to change color</p>
              </div>
            </div>
          </div>

          {/* Size and Material Selection */}
          <div className="grid grid-cols-2 gap-6">
            {/* Size Selection */}
            <div className="space-y-3">
              <label htmlFor="size-select" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Size
              </label>
              <select
                id="size-select"
                name="size"
                value={formData.size}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 cursor-pointer"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="extra-large">Extra Large</option>
              </select>
            </div>

            {/* Material Selection */}
            <div className="space-y-3">
              <label htmlFor="material-select" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Material
              </label>
              <select
                id="material-select"
                name="material"
                value={formData.material}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 cursor-pointer"
              >
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
                <option value="eco-friendly">Eco-Friendly</option>
                <option value="deluxe">Deluxe</option>
              </select>
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Upload Custom Image
            </label>

            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden border border-orange-200">
                <img
                  src={imagePreview}
                  alt="Custom upload preview"
                  className="w-full h-40 object-cover"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                  aria-label="Remove uploaded image"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-teal-400 bg-teal-50'
                    : 'border-orange-300 hover:border-teal-300 bg-white bg-opacity-60'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">
                  Drag and drop an image here, or
                </p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  <Upload className="w-5 h-5" />
                  Choose File
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  aria-label="Upload custom image file"
                />
                <p className="text-xs text-gray-500 mt-3">
                  PNG, JPG, GIF up to 10MB
                </p>
              </div>
            )}
          </div>

          {/* Additional Notes */}
          <div className="space-y-3">
            <label htmlFor="notes" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Additional Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Any special instructions or requests..."
              rows={4}
              className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 resize-none"
              maxLength={200}
            />
            <p className="text-xs text-gray-600 flex justify-between items-center">
              <span>Share any special requirements</span>
              <span className="font-medium">{formData.notes.length}/200</span>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-8 pb-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-8 py-4 bg-white bg-opacity-80 text-gray-700 rounded-xl hover:bg-opacity-100 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-400 font-semibold border border-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-teal-400 font-semibold"
            >
              Save Customization
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CustomizationModal;
